import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";

export default function HeroCarousel({ items }: { items: NewsItem[] }) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const goTo = useCallback((i: number) => setIndex(((i % count) + count) % count), [count]);

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((v) => (v + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  return (
    <section className="relative w-full rounded-lg overflow-hidden h-[440px] md:h-[560px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 小泽玛利亚焦点资讯`}
            className="w-full h-full object-top object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/10"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex flex-col justify-end p-5 md:p-10">
        <span className="inline-flex items-center self-start gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs mb-3">
          <i className="ri-flashlight-fill"></i>
          焦点资讯
        </span>
        <h2 className="font-heading text-xl md:text-4xl font-semibold text-white leading-snug max-w-3xl mb-3">
          {items[index].title}
        </h2>
        <p className="text-sm md:text-base text-white/85 leading-relaxed max-w-2xl line-clamp-2 mb-5">
          {items[index].summary}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to={`/detail/${items[index].id}`}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-background-50 text-foreground-950 text-sm font-medium hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看详情
            <i className="ri-arrow-right-line"></i>
          </Link>
          <span className="text-xs text-white/75">{items[index].date}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition-colors cursor-pointer"
        aria-label="上一条"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition-colors cursor-pointer"
        aria-label="下一条"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-5 right-5 md:bottom-10 md:right-10 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第 ${i + 1} 条`}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-background-50" : "w-2 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}